import {GRAPHYO_MAX_LENGTH,graphyoAnswerError} from "./graphyo-activity";
import {titrationLabelingAnswerError} from './titration-labeling';
import {densityConcentrations,densityValues,type ClassDensityValues,type ClassDensityChange} from './graphyo-density';
import {chemicalLaboratoryId} from './chemical-lab-config';
import {chemicalMeasurementRows,mergeChemicalMeasurements} from './chemical-lab-measurements';
import {chemicalLabAction,initialChemicalWorld,parseChemicalWorld,publicChemicalWorld,serializeChemicalWorld} from './chemical-lab-session';
import type {WorldAction} from './mini-lab-mechanics';
import {measurementEngineId,decodeExperiment,serializePublicExperimentConfig,parseExperimentState,type ExperimentAction} from './measurement-engine';
import {experimentAnswers} from './measurement-engine-answers';
import {savedManipulation} from './activity-response-policy';
export {allowsAnswerAttachments,allowsAnswerAttachmentsForActivity} from './activity-response-policy';
import {measuredAbsorbance,spectroId} from './resources/chemistry-contract';
import {applyMeasurementEvent,sourceMeasurements} from './resource-measurements';
import { checkpointEvidenceIds, checkpointAnchorId } from "./interactive-activity-checkpoints";
import {validateExperimentConfig, validateSeries} from './resources/measurement-contract';
import {hasProduction, resultsForMember, setMemberAttendance, type AttendanceRecord} from "./attendance-results";
import {trackingDetails} from "./session-tracking";
import {canConsultCopy, copyReleased, evidenceLocked} from "./copy-permissions";
export {canConsultCopy, copyReleased, evidenceLocked, isRestitutionOnlyChange} from "./copy-permissions";
import {attemptSessionId, latestWork, hasSavedWork} from "./activity-continuity";
export {attemptSessionId} from "./activity-continuity";
import {freeAccess, setClassAccess, type ClassAccessCommand} from "./class-assignments";
export {freeAccess} from "./class-assignments";
import {normalizeActivity, normalizeActivityDraft, activityPackageForEditing, type UnifiedActivity, type ContentCheckpoint} from "./normalized-activity";
import {validPhotoStrokes, type PhotoAnnotation, type PhotoStroke} from "./photo-annotations";
import type {
  CatalogCompetency,
  ActivityDefinition,
  ValidationCriteria,
} from "./catalog";
import { defaultCurriculum } from "./curriculum";
import type { Curriculum } from "./curriculum";
import type { InteractiveActivityPackage } from "./interactive-activity-package";
import { validateInteractiveActivityPackage } from "./interactive-activity-package";

export type ActivityType = "standard" | "competency_only" | "interactive";
export type AttemptStatus =
  "not_started" | "in_progress" | "submitted" | "reviewed" | "completed";
export type CompetenceLevel = "A" | "B" | "C" | "D" | "ABS" | "NE";
export type StudentRef = { classId: string; studentId: string };
export type LibraryActivity = {
  id: string;
  title: string;
  levelId: string;
  themeId: string;
  chapterId: string;
  levelTitle: string;
  themeTitle: string;
  chapterTitle: string;
  activityType: ActivityType;
  coefficient: number;
  competenceIds: string[];
  version: number;
  estimatedMinutes: number | null;
  archived: boolean;
  updatedAt: string;
};
export type ActivityCheckpoint = {
  flowPosition?: number;
  id: string;
  competencyId: string;
  questionIds?: string[];
  evidenceBlockIds?: string[];
  positionAfterBlockId?: string;
  rubric: ValidationCriteria;
  competenceCode: string;
  competenceLabel: string;
};
// One immutable snapshot per version, shared by every attempt using that version.
export type ActivityVersion = LibraryActivity & {
  blocks?: UnifiedActivity["blocks"];
  assets?: UnifiedActivity["assets"];
  competencyCheckpoints?: ContentCheckpoint[];
  package: InteractiveActivityPackage | null;
  checkpoints: ActivityCheckpoint[];
};
export type Assignment = {
  classAssociation?: boolean;
  active?: boolean;
  visible?: boolean;
  sessionId?: string;
  id: string;
  activityId: string;
  targetType: "student" | "students" | "group" | "class";
  targetId: string;
  recipients: StudentRef[];
  assignedAt: string;
  dueDate: string | null;
};
export type Evidence = {
  id: string;
  sourceId: string;
  competencyId: string;
  value: string;
  recordedAt: string;
  kind: "answer" | "interaction" | "observation";
};
export type CompetenceResult = {
  checkpointId: string;
  competencyId: string;
  level: CompetenceLevel;
  comment: string;
  validatedAt: string;
};
export type HelpUsage = { helpUsed: true; helpOpenedAt: string; sourceBlockId: string; answerSnapshot: string; photoIdsSnapshot?: string[]; progressive?: boolean; maxHelpLevel?: number; helpHistory?: {level:number;openedAt:string}[] };
export type PhotoRotation = 0 | 90 | 180 | 270;
export type AnswerPhoto = {id: string; name: string; mimeType: string; url: string; createdAt: string; rotation?: PhotoRotation};
export type StudentAttempt = StudentRef & {
  restitution?: {status: "released"; releasedAt: string; releasedBy: string};
  /** Server projection; never accepted as input. */
  copyAccessible?: boolean;
  checkpointValidations?: Record<string, {locked: boolean; evidenceBlockIds: string[];
    events: {action: "validate" | "unlock"; by: string; at: string; level?: CompetenceLevel; sessionId?: string}[]}>;
  attendance?: Record<string, AttendanceRecord>;
  focusGuard?: {generation: number; blocked: boolean; events: {id: string; reason: "hidden" | "blur"; studentId: string; at: string; releasedAt?: string}[]};
  /** Current access window; the original session and the owner snapshot remain immutable. */
  currentSessionId?: string;
  /** A new evaluation continues a frozen archive, without displaying a second activity. */
  continuedFromAttemptId?: string;
  ownerType?: "student" | "group";
  groupId?: string;
  groupName?: string;
  memberIdsSnapshot?: string[];
  sessionId?: string;
  lastSavedAt?: string;
  id: string;
  activityId: string;
  activityVersion: number;
  assignmentId: string;
  status: AttemptStatus;
  startedAt: string | null;
  submittedAt: string | null;
  completedAt: string | null;
  answers: Record<string, string>;
  /** Server-owned attachments, uploaded atomically with the photo storage. */
  photos?: Record<string, AnswerPhoto[]>;
  photoAnnotations?: Record<string, PhotoAnnotation>;
  teacherTextAnnotations?: Record<string, {text: string; updatedAt: string}>;
  questionVerdicts?: Record<string, {state: "green" | "orange" | "red"; updatedAt: string}>;
  teacherCorrectionOverrides?: Record<string, {text: string; updatedAt: string}>;
  /** Absence means the help has not been opened in this reader. */
  helpUsage?: Record<string, HelpUsage>;
  evidence: Evidence[];
  competenceResults: Record<string, CompetenceResult>;
  feedback: string[];
  grade: number | null;
  durationSeconds: number | null;
  revision: number;
};
export type CompetenceHistory = StudentRef &
  CompetenceResult & {
    id: string;
    attemptId: string;
    activityId: string;
    activityVersion: number;
    activityTitle: string;
    competenceCode: string;
    competenceLabel: string;
    finalizedAt: string;
    source?: "legacy_session" | "attendance_auto";
  };
export type LibraryState = {
  deletedActivities?: {id:string; deletedAt:string}[];
  digitalSessions?: DigitalSession[];
  activities: LibraryActivity[];
  versions: ActivityVersion[];
  assignments: Assignment[];
  attempts: StudentAttempt[];
  history: CompetenceHistory[];
};
export const emptyLibrary = (): LibraryState => ({
  digitalSessions: [],
  activities: [],
  versions: [],
  assignments: [],
  attempts: [],
  history: [],
});
export const sameStudent = (a: StudentRef, b: StudentRef) =>
  a.classId === b.classId && a.studentId === b.studentId;
export type SessionParticipationLevel = "A" | "B" | "C" | "D" | "ABS";
export type DigitalSession = {
  sharedGraphyo?: Record<string,ClassDensityValues>;
  id: string; classId: string; activityId: string; activityVersion: number;
  access: "active" | "paused" | "ended"; activeUntil: string;
  startedAt: string; endedAt: string | null; revision: number;
  participation?: Record<string, {level: SessionParticipationLevel; updatedAt: string}>;
  attemptIds?: string[];
};
export const attemptMemberIds = (a: StudentAttempt): string[] =>
  a.ownerType === "group" ? a.memberIdsSnapshot ?? [] : [a.studentId];
export const ownsAttempt = (a: StudentAttempt, student: StudentRef) =>
  a.classId === student.classId && attemptMemberIds(a).includes(student.studentId);
export const sessionAccess = (session: DigitalSession, now = new Date().toISOString()) =>
  session.access === "active" && Date.parse(session.activeUntil) <= Date.parse(now) ? "paused" : session.access;
export function attemptAccess(s: LibraryState, a: StudentAttempt, now = new Date().toISOString()) {
  if (!attemptSessionId(a)) return "active";
  const session = s.digitalSessions?.find(d => d.id === attemptSessionId(a));
  return session ? sessionAccess(session, now) : "paused";
}
export function attemptProgress(s: LibraryState, a: StudentAttempt) {
  const {expected, completed} = trackingDetails(versionFor(s, a), a);
  return {expected, completed, lastSavedAt: a.lastSavedAt ?? a.startedAt};
}
export const versionFor = (
  s: LibraryState,
  a: Pick<StudentAttempt, "activityId" | "activityVersion">,
) =>
  s.versions.find(
    (v) => v.id === a.activityId && v.version === a.activityVersion,
  )!;
export type LibraryActor =
  { role: "teacher"; teacherId?: string } | ({ role: "student" } & StudentRef);
export type PublishActivity = {
  blocks?: UnifiedActivity["blocks"];
  assets?: UnifiedActivity["assets"];
  competencyCheckpoints?: ContentCheckpoint[];
  id: string;
  expectedVersion: number;
  title: string;
  activityType?: ActivityType;
  coefficient?: number;
  levelId: string;
  themeId: string;
  chapterId: string;
  levelTitle: string;
  themeTitle: string;
  chapterTitle: string;
  competenceIds: string[];
  estimatedMinutes: number | null;
  package: InteractiveActivityPackage | null;
  criteria?: Record<string, ValidationCriteria>;
};
export type LibraryCommand =
  | {type:"share_graphyo";attemptId:string;expectedRevision:number;blockId:string;cells:ClassDensityChange[]}
  | {type:"chemical_lab_action";attemptId:string;expectedRevision:number;blockId:string;action:WorldAction}
  | {type:"experiment_action";attemptId:string;expectedRevision:number;blockId:string;action:ExperimentAction}
  | {type: "validate_checkpoint"; attemptId: string; expectedRevision: number; checkpointId: string; level: CompetenceLevel}
  | {type: "unlock_checkpoint"; attemptId: string; expectedRevision: number; checkpointId: string}
  | {type: "release_copy"; attemptId: string; expectedRevision: number}
  | {type: "release_class"; activityId: string; classId: string}
  | {type: "complete_class"; activityId: string; classId: string}
  | {type: "session_attendance"; sessionId: string; studentId: string; absent: boolean; expectedAbsent: boolean}
  | {type: "session_participation"; sessionId: string; studentId: string; level: SessionParticipationLevel; expectedRevision: number}
  | {type: "session_participation_present"; sessionId: string; level: SessionParticipationLevel; expectedRevision: number}
  | {type: "bulk_checkpoint_grade"; activityId: string; activityVersion: number; classId: string; checkpointId: string; level: CompetenceLevel}
  | {type: "focus_incident"; attemptId: string; sessionId: string; generation: number; incidentId: string; reason: "hidden" | "blur"}
  | {type: "focus_release"; attemptId: string; generation: number}
  | {type: "delete_evaluation"; activityId: string; classId: string; attempts: {id: string; revision: number}[]}
  | ClassAccessCommand
  | {type: "launch_session"; sessionId: string; activityId: string; expectedVersion: number; classId: string;
      memberIds: string[]; groups: {id: string; name: string; memberIds: string[]}[]}
  | {type: "session_access"; sessionId: string; expectedRevision: number; action: "pause" | "resume" | "finish"}
  | { type: "annotate_photo"; attemptId: string; expectedRevision: number; photoId: string; strokes: PhotoStroke[] }
  | { type: "rotate_photo"; attemptId: string; expectedRevision: number; photoId: string }
  | {type:"set_question_verdict"; attemptId:string; expectedRevision:number; blockId:string; state:"green"|"orange"|"red"}
  | { type: "annotate_text"; attemptId: string; expectedRevision: number; blockId: string; text: string }
  | { type: "measure_instrument"; attemptId:string; expectedRevision:number; blockId:string; solutionId:string; wavelengthNm:number }
  | { type: "set_correction_override"; attemptId: string; expectedRevision: number; blockId: string; text: string }
  | { type: "open_help"; attemptId: string; expectedRevision: number; blockId: string; helpLevel?: number; sourceAnswer: string; answers?: Record<string, string> }
  | { type: "publish"; activity: PublishActivity }
  | { type: "archive"; activityId: string; archived: boolean }
  | { type: "delete_activity"; activityId: string }
  | { type: "assign"; assignment: Omit<Assignment, "assignedAt"> }
  | { type: "start"; assignmentId: string; student: StudentRef }
  | {
      type: "answer";
      attemptId: string;
      expectedRevision: number;
      answers: Record<string, string>;
    }
  | {
      type: "evidence";
      attemptId: string;
      expectedRevision: number;
      evidence: Omit<Evidence, "recordedAt">;
    }
  | { type: "submit"; attemptId: string; expectedRevision: number }
  | {
      type: "review";
      attemptId: string;
      expectedRevision: number;
      results: {
        checkpointId: string;
        level: CompetenceLevel;
        comment: string;
      }[];
      feedback: string[];
      grade: number | null;
    }
  | { type: "complete"; attemptId: string; expectedRevision: number };

function assert(condition: unknown, message: string): asserts condition {
  if (!condition) throw new Error(message);
}
function text(value: unknown): value is string {
  return (
    typeof value === "string" && value.trim().length > 0 && value.length <= 1000
  );
}
function teacher(actor: LibraryActor) {
  assert(actor.role === "teacher", "Action réservée au professeur.");
}
const levels = ["A", "B", "C", "D", "ABS", "NE"];

// Pure, shared command handler. No UI can mutate a finalized attempt or append history directly.
function finalizeAttempt(
  state: LibraryState,
  attempt: StudentAttempt,
  version: ActivityVersion,
  now: string,
) {
  attempt.status = "completed";
  attempt.completedAt = now;
  attempt.durationSeconds = attempt.startedAt
    ? Math.max(
        0,
        Math.round(
          (Date.parse(attempt.submittedAt ?? now) - Date.parse(attempt.startedAt)) /
            1000,
        ),
      )
    : null;
  for (const studentId of attemptMemberIds(attempt))
    for (const result of Object.values(resultsForMember(attempt, studentId))) {
      const cp = version.checkpoints.find((c) => c.id === result.checkpointId)!;
      state.history.push({
        ...result,
        id:
          attempt.ownerType === "group"
            ? `${attempt.id}:${studentId}:${cp.id}`
            : `${attempt.id}:${cp.id}`,
        studentId,
        classId: attempt.classId,
        attemptId: attempt.id,
        activityId: attempt.activityId,
        activityVersion: attempt.activityVersion,
        activityTitle: version.title,
        competenceCode: cp.competenceCode,
        competenceLabel: cp.competenceLabel,
        finalizedAt: now,
      });
    }
}

export function applyLibraryCommand(
  source: LibraryState,
  command: LibraryCommand,
  actor: LibraryActor,
  catalog: CatalogCompetency[],
  now = new Date().toISOString(),
): LibraryState {
  const s = structuredClone(source);
  if (command.type === "complete_class") {
    teacher(actor);
    const copies = s.attempts.filter(
      (attempt) =>
        attempt.activityId === command.activityId &&
        attempt.classId === command.classId,
    );
    assert(copies.length > 0, "Aucune copie à finaliser.");
    assert(
      copies.every((attempt) =>
        ["submitted", "reviewed", "completed"].includes(attempt.status),
      ),
      "Termine d’abord l’activité : certaines copies sont encore en réalisation.",
    );
    for (const attempt of copies) {
      if (attempt.status === "completed") continue;
      if (attempt.status === "submitted") attempt.status = "reviewed";
      const version = versionFor(s, attempt);
      finalizeAttempt(s, attempt, version, now);
      attempt.revision += 1;
      attempt.lastSavedAt = now;
    }
    return s;
  }
  if (command.type === "release_copy" || command.type === "release_class") {
    teacher(actor);
    assert(actor.role === "teacher" && text(actor.teacherId), "Identité professeur requise.");
    const copies = command.type === "release_copy" ? s.attempts.filter(a => a.id === command.attemptId) :
      s.attempts.filter(a => a.activityId === command.activityId && a.classId === command.classId && a.status === "completed");
    if (command.type === "release_copy") {
      assert(copies.length === 1 && copies[0].status === "completed", "Seules les copies finalisées peuvent être restituées.");
      if (!copyReleased(copies[0])) assert(copies[0].revision === command.expectedRevision, "Ce travail a changé. Recharge avant de réessayer.");
    }
    for (const a of copies) if (!copyReleased(a)) {
      a.restitution = {status: "released", releasedAt: now, releasedBy: actor.teacherId}; a.revision++;
    }
    return s;
  }
  if (command.type === "validate_checkpoint" || command.type === "unlock_checkpoint") {
    teacher(actor);
    assert(actor.role === "teacher" && text(actor.teacherId), "Identité professeur requise.");
    const a = s.attempts.find(a => a.id === command.attemptId);
    assert(a && a.status !== "completed", "Copie introuvable ou finalisée.");
    assert(a.revision === command.expectedRevision, "Ce travail a changé. Recharge avant de réessayer.");
    const v = versionFor(s, a), cp = v.checkpoints.find(c => c.id === command.checkpointId);
    assert(cp, "Checkpoint inconnu.");
    a.checkpointValidations ??= {};
    const validation = a.checkpointValidations[cp.id] ??= {locked: false, evidenceBlockIds: checkpointEvidenceIds(cp), events: []};
    if (command.type === "validate_checkpoint") {
      assert(["not_started","in_progress"].includes(a.status) && attemptAccess(s,a,now) !== "ended", "La validation en direct nécessite une activité accessible.");
      assert(levels.includes(command.level), "Niveau invalide.");
      validation.locked = true;
      validation.events.push({action: "validate", by: actor.teacherId, at: now, level: command.level, sessionId: attemptSessionId(a)});
      a.competenceResults[cp.id] = {checkpointId: cp.id, competencyId: cp.competencyId, level: command.level, comment: "", validatedAt: now};
      if (a.ownerType !== "group" && a.attendance?.[a.studentId]) delete a.attendance[a.studentId].results[cp.id];
    } else {
      assert(validation.locked, "Ce checkpoint n’est pas verrouillé.");
      validation.locked = false; validation.events.push({action: "unlock", by: actor.teacherId, at: now, sessionId: attemptSessionId(a)});
      // The decision remains in the audit; revised evidence must be validated again.
      delete a.competenceResults[cp.id];
    }
    a.revision++; a.lastSavedAt = now; return s;
  }
  if (command.type === "bulk_checkpoint_grade") {
    teacher(actor);
    assert(["A", "B", "C", "D", "NE"].includes(command.level), "Le mode « Tous les présents » ne peut pas attribuer Absent.");
    const copies = s.attempts.filter(
      (attempt) =>
        attempt.classId === command.classId &&
        attempt.activityId === command.activityId &&
        attempt.activityVersion === command.activityVersion &&
        attempt.status !== "completed",
    );
    assert(copies.length > 0, "Aucune copie modifiable pour cette classe.");
    let changed = 0;
    for (const attempt of copies) {
      const version = versionFor(s, attempt);
      const checkpoint = version.checkpoints.find((item) => item.id === command.checkpointId);
      if (!checkpoint) continue;
      const direct = normalizeActivity(version).capabilities.supportsDirectEvaluation;
      if (!["submitted", "reviewed"].includes(attempt.status) && !direct) continue;
      const presentMembers = attemptMemberIds(attempt).filter(
        (studentId) => !attempt.attendance?.[studentId]?.absent,
      );
      if (!presentMembers.length) continue;
      const previous = attempt.competenceResults[checkpoint.id];
      attempt.competenceResults[checkpoint.id] = {
        checkpointId: checkpoint.id,
        competencyId: checkpoint.competencyId,
        level: command.level,
        comment: previous?.comment ?? "",
        validatedAt: now,
      };
      if (
        attempt.ownerType !== "group" &&
        attempt.attendance?.[attempt.studentId] &&
        !attempt.attendance[attempt.studentId].absent
      )
        delete attempt.attendance[attempt.studentId].results[checkpoint.id];
      if (attempt.status === "submitted") attempt.status = "reviewed";
      attempt.revision += 1;
      attempt.lastSavedAt = now;
      changed += 1;
    }
    assert(changed > 0, "Aucun élève présent avec une copie modifiable.");
    return s;
  }
  if (command.type === "session_participation_present") {
    teacher(actor);
    const session = s.digitalSessions?.find(d => d.id === command.sessionId);
    assert(session, "Séance inconnue.");
    assert(session.revision === command.expectedRevision, "La séance a changé. Actualise avant de modifier la participation.");
    assert(["A", "B", "C", "D"].includes(command.level), "La participation collective s’applique uniquement aux présents.");
    const absentIds = new Set(
      s.attempts
        .filter((attempt) => attemptSessionId(attempt) === session.id)
        .flatMap((attempt) =>
          attemptMemberIds(attempt).filter(
            (studentId) => attempt.attendance?.[studentId]?.absent,
          ),
        ),
    );
    const participation = {...(session.participation ?? {})};
    const presentIds = Object.keys(participation).filter(
      (studentId) => !absentIds.has(studentId) && participation[studentId]?.level !== "ABS",
    );
    assert(presentIds.length > 0, "Aucun élève présent dans cette séance.");
    let changed = false;
    for (const studentId of presentIds) {
      if (participation[studentId]?.level === command.level) continue;
      participation[studentId] = {level: command.level, updatedAt: now};
      changed = true;
    }
    if (!changed) return s;
    session.participation = participation;
    session.revision += 1;
    return s;
  }
  if (command.type === "session_participation") {
    teacher(actor);
    const session = s.digitalSessions?.find(d => d.id === command.sessionId);
    assert(session, "Séance inconnue.");
    assert(session.revision === command.expectedRevision, "La séance a changé. Actualise avant de modifier la participation.");
    assert(["A","B","C","D","ABS"].includes(command.level), "Participation invalide.");
    assert(Object.hasOwn(session.participation ?? {}, command.studentId), "Élève absent de cette séance.");
    const previous = session.participation?.[command.studentId];
    if (previous?.level === command.level) return s;
    session.participation = {...session.participation, [command.studentId]: {level: command.level, updatedAt: now}};
    session.revision += 1;
    return s;
  }
  if (command.type === "session_attendance") {
    teacher(actor);
    const session = s.digitalSessions?.find(d => d.id === command.sessionId);
    assert(session && typeof command.absent === "boolean" && typeof command.expectedAbsent === "boolean", "Absence invalide.");
    const copies = s.attempts.filter(a => attemptSessionId(a) === session.id && attemptMemberIds(a).includes(command.studentId));
    assert(copies.length === 1, "Élève introuvable ou copies ambiguës dans cette séance.");
    const a = copies[0];
    assert(a.status !== "completed", "Une réalisation terminée est figée.");
    const absent = a.attendance?.[command.studentId]?.absent ?? false;
    if (absent === command.absent) return s;
    assert(absent === command.expectedAbsent, "La présence a changé. Actualise la séance.");
    if (setMemberAttendance(a, versionFor(s, a), command.studentId, command.absent, now)) a.revision += 1;
    return s;
  }
  if (command.type === "focus_incident" || command.type === "focus_release") {
    const a = s.attempts.find(a => a.id === command.attemptId);
    assert(a && (actor.role === "teacher" || ownsAttempt(a, actor)), "Réalisation inaccessible.");
    const guard = a.focusGuard ?? {generation: 0, blocked: false, events: []};
    if (command.type === "focus_release") {
      teacher(actor);
      assert(a.status !== "completed", "Une réalisation terminée est figée.");
      assert(command.generation === guard.generation, "L’incident a changé. Actualise le suivi.");
      if (!guard.blocked) return s;
      guard.blocked = false;
      guard.generation += 1;
      const last = guard.events.at(-1);
      if (last) last.releasedAt = now;
    } else {
      assert(actor.role === "student", "Signalement réservé au lecteur élève.");
      assert(text(command.incidentId) && ["hidden", "blur"].includes(command.reason), "Incident invalide.");
      assert(Number.isInteger(command.generation) && command.generation >= 0, "Incident invalide.");
      // Ignore retries from an earlier session or before the teacher's release.
      if (command.sessionId !== attemptSessionId(a) || a.status !== "in_progress" || attemptAccess(s, a, now) !== "active" || command.generation < guard.generation) return s;
      assert(command.generation === guard.generation, "Incident invalide.");
      if (guard.blocked || guard.events.some(e => e.id === command.incidentId)) return s;
      guard.blocked = true;
      guard.events.push({id: command.incidentId, reason: command.reason, studentId: actor.studentId, at: now});
    }
    a.focusGuard = guard;
    a.revision += 1;
    return s;
  }
  if (command.type === "delete_evaluation") {
    teacher(actor);
    const selected = s.attempts.filter(a => a.activityId === command.activityId && a.classId === command.classId);
    assert(selected.length && Array.isArray(command.attempts) && selected.length === command.attempts.length && selected.every(a => command.attempts.some(expected => expected.id === a.id && expected.revision === a.revision)), "Les copies ont changé. Actualise avant de supprimer.");
    assert(!s.digitalSessions?.some(d => d.activityId === command.activityId && d.classId === command.classId && d.access !== "ended"), "Termine la séance avant de supprimer cette évaluation.");
    const ids = new Set(selected.map(a => a.id));
    s.attempts = s.attempts.filter(a => !ids.has(a.id));
    s.history = s.history.filter(h => !ids.has(h.attemptId));
    s.assignments = s.assignments.flatMap(a => {
      if (a.activityId !== command.activityId || a.classAssociation) return [a];
      const recipients = a.recipients.filter(r => r.classId !== command.classId);
      return recipients.length ? [{...a, recipients}] : [];
    });
    return s;
  }
  if (command.type === "delete_activity") {
    teacher(actor);
    const activity = s.activities.find((item) => item.id === command.activityId);
    assert(activity, "Activité inconnue.");
    const removedAttempts = new Set(s.attempts.filter(a=>a.activityId===command.activityId).map(a=>a.id));
    s.attempts = s.attempts.filter(a=>!removedAttempts.has(a.id));
    s.history = s.history.filter(h=>h.activityId!==command.activityId && !removedAttempts.has(h.attemptId));
    s.deletedActivities = [...(s.deletedActivities??[]).filter(a=>a.id!==command.activityId),{id:command.activityId,deletedAt:now}];
    s.activities = s.activities.filter((item) => item.id !== command.activityId);
    s.versions = s.versions.filter((version) => version.id !== command.activityId);
    s.assignments = s.assignments.filter((assignment) => assignment.activityId !== command.activityId);
    s.digitalSessions = s.digitalSessions?.filter((session) => session.activityId !== command.activityId);
    return s;
  }
  if (command.type === "class_access") { teacher(actor); return setClassAccess(s, command, now); }
  if (command.type === "launch_session") {
    teacher(actor);
    assert(text(command.sessionId) && text(command.classId), "Séance invalide.");
    const previous = s.digitalSessions?.find(d => d.id === command.sessionId);
    if (previous) {
      assert(previous.classId === command.classId && previous.activityId === command.activityId, "Identifiant de séance déjà utilisé.");
      return s;
    }
    const activity = s.activities.find(a => a.id === command.activityId && !a.archived);
    assert(activity && activity.version === command.expectedVersion, "L’activité a changé. Recharge avant de démarrer.");
    const version = versionFor(s, {activityId: activity.id, activityVersion: activity.version});
    const capabilities = normalizeActivity(version).capabilities;
    assert(capabilities.hasStudentInputs || capabilities.hasCheckpoints, "Cette activité ne contient ni production ni compétence à suivre.");
    assert(Array.isArray(command.memberIds) && command.memberIds.length > 0 && command.memberIds.every(text) && new Set(command.memberIds).size === command.memberIds.length, "Effectif invalide.");
    assert(Array.isArray(command.groups), "Groupes invalides.");
    const grouped = new Set<string>();
    const groupIds = new Set<string>();
    for (const group of command.groups) {
      assert(text(group.id) && text(group.name) && !groupIds.has(group.id) && Array.isArray(group.memberIds) && group.memberIds.length > 0, "Groupe invalide.");
      groupIds.add(group.id);
      for (const id of group.memberIds) {
        assert(command.memberIds.includes(id) && !grouped.has(id), "Un élève ne peut appartenir qu’à un groupe de cette séance.");
        grouped.add(id);
      }
    }
    assert(!s.attempts.some(a => attemptSessionId(a) && a.classId === command.classId && a.activityId === activity.id &&
      attemptAccess(s, a, now) !== "ended" && attemptMemberIds(a).some(id => command.memberIds.includes(id))),
      "Une séance existe déjà pour ces élèves et cette activité. Reprends-la.");
    const session: DigitalSession = {id: command.sessionId, classId: command.classId, activityId: activity.id,
      activityVersion: activity.version, access: "active", startedAt: now, endedAt: null,
      activeUntil: new Date(Date.parse(now) + 3 * 60 * 60 * 1000).toISOString(), revision: 1,
      participation: Object.fromEntries(command.memberIds.map(id => [id, {level: "B" as const, updatedAt: now}])),
      attemptIds: []};
    (s.digitalSessions ??= []).push(session);
    const owners = capabilities.supportsDirectEvaluation
      ? command.memberIds.map(id => ({ownerType: "student" as const, groupId: undefined, groupName: undefined, ids: [id]}))
      : [
          ...command.groups.map(g => ({ownerType: "group" as const, groupId: g.id, groupName: g.name, ids: g.memberIds})),
          ...command.memberIds.filter(id => !grouped.has(id)).map(id => ({ownerType: "student" as const, groupId: undefined, groupName: undefined, ids: [id]})),
        ];
    const hasPreservedAttemptData = (attempt: StudentAttempt) =>
      hasProduction(attempt) ||
      Object.keys(attempt.competenceResults ?? {}).length > 0 ||
      Object.values(attempt.checkpointValidations ?? {}).some(validation => validation.events.length > 0) ||
      attempt.feedback.length > 0 ||
      attempt.grade !== null ||
      Object.keys(attempt.teacherTextAnnotations ?? {}).length > 0 ||
      Object.keys(attempt.teacherCorrectionOverrides ?? {}).length > 0 ||
      Object.keys(attempt.questionVerdicts ?? {}).length > 0 ||
      Object.values(attempt.attendance ?? {}).some(record => Object.keys(record.results ?? {}).length > 0);
    const priors = owners.map(owner => {
      const priorByMember = owner.ids.map(id => latestWork(s, s.attempts.filter(a => a.classId === command.classId && a.activityId === activity.id && ownsAttempt(a, {classId: command.classId, studentId: id}))));
      const prior = priorByMember.find(Boolean);
      if (prior) {
        const sameOwner = priorByMember.every(a => a?.id === prior.id) &&
          attemptMemberIds(prior).length === owner.ids.length &&
          attemptMemberIds(prior).every(id => owner.ids.includes(id));
        if (!sameOwner) {
          const conflicting = priorByMember.filter((a): a is StudentAttempt => Boolean(a));
          if (conflicting.every(attempt => !hasPreservedAttemptData(attempt))) return undefined;
        }
        assert(sameOwner,
          "Ces élèves ont déjà du travail enregistré dans des copies différentes ou un autre groupe. Reconstitue le groupe de la copie pour la continuer ; aucune réponse n’a été déplacée.");
      }
      return prior;
    });
    const priorVersions = new Set(priors.flatMap(a => a ? [a.activityVersion] : []));
    assert(priorVersions.size <= 1, "Les copies utilisent des versions différentes. Reprends-les séparément.");
    session.activityVersion = [...priorVersions][0] ?? activity.version;
    for (const [index, owner] of owners.entries()) {
      const prior = priors[index];
      if (prior) {
        if (!["completed", "reviewed"].includes(prior.status)) {
          prior.currentSessionId = session.id;
          for (const studentId of owner.ids)
            if (prior.attendance?.[studentId]?.absent)
              setMemberAttendance(prior, versionFor(s, prior), studentId, false, now);
          prior.status = capabilities.supportsDirectEvaluation || hasSavedWork(prior) || prior.startedAt ? "in_progress" : "not_started";
          prior.startedAt ??= now;
          prior.submittedAt = null;
          prior.lastSavedAt = now;
          prior.revision += 1;
          session.attemptIds!.push(prior.id);
          continue;
        }
      }
      const assignmentId = crypto.randomUUID();
      s.assignments.push({id: assignmentId, activityId: activity.id, sessionId: session.id,
        targetType: owner.ownerType === "group" ? "students" : "student", targetId: owner.groupId ?? owner.ids[0],
        recipients: owner.ids.map(studentId => ({classId: command.classId, studentId})), assignedAt: now, dueDate: null});
      const attemptId = crypto.randomUUID();
      s.attempts.push({id: attemptId, assignmentId, sessionId: session.id, classId: command.classId,
        studentId: owner.ownerType === "student" ? owner.ids[0] : "", ownerType: owner.ownerType,
        ...(owner.ownerType === "group" ? {groupId: owner.groupId, groupName: owner.groupName, memberIdsSnapshot: [...owner.ids]} : {}),
        ...(prior ? {continuedFromAttemptId: prior.id, photos: structuredClone(prior.photos ?? {}), helpUsage: structuredClone(prior.helpUsage ?? {})} : {}),
        activityId: activity.id, activityVersion: session.activityVersion,
        status: capabilities.supportsDirectEvaluation || (prior && (hasSavedWork(prior) || prior.startedAt)) ? "in_progress" : "not_started",
        startedAt: capabilities.supportsDirectEvaluation || prior ? now : null,
        submittedAt: null, completedAt: null, answers: structuredClone(prior?.answers ?? {}), evidence: structuredClone(prior?.evidence ?? []), competenceResults: {}, feedback: [],
        grade: null, durationSeconds: null, revision: 1});
      session.attemptIds!.push(attemptId);
    }
    return s;
  }
  if (command.type === "session_access") {
    teacher(actor);
    const session = s.digitalSessions?.find(d => d.id === command.sessionId);
    assert(session, "Séance inconnue.");
    if (session.access === "ended" && command.action === "finish") return s;
    assert(session.access !== "ended" && command.expectedRevision === session.revision, "La séance a changé. Recharge avant de réessayer.");
    assert(["pause", "resume", "finish"].includes(command.action), "Action de séance invalide.");
    session.access = command.action === "resume" ? "active" : command.action === "pause" ? "paused" : "ended";
    if (command.action === "resume") session.activeUntil = new Date(Date.parse(now) + 3 * 60 * 60 * 1000).toISOString();
    if (command.action === "finish") {
      session.endedAt = now;
      for (const a of s.attempts.filter(a => attemptSessionId(a) === session.id && ["not_started", "in_progress"].includes(a.status))) {
        a.status = "submitted"; a.submittedAt = now; a.revision += 1;
      }
    }
    session.revision += 1;
    return s;
  }
  if (command.type === "publish") {
    assert(!s.deletedActivities?.some(a=>a.id===command.activity.id), "Cette activité a été supprimée. Crée une nouvelle activité pour la republier.");
    teacher(actor);
    const p = command.activity;
    assert(
      p &&
        [
          p.id,
          p.title,
          p.levelId,
          p.themeId,
          p.chapterId,
          p.levelTitle,
          p.themeTitle,
          p.chapterTitle,
        ].every(text),
      "Titre et classement complets requis.",
    );
    assert(
      p.activityType === undefined || ["standard", "interactive", "competency_only"].includes(p.activityType),
      "Type d’activité invalide.",
    );
    assert(
      Array.isArray(p.competenceIds) &&
        new Set(p.competenceIds).size === p.competenceIds.length &&
        p.competenceIds.every((id) =>
          catalog.some((c) => c.id === id && c.schoolLevel === p.levelTitle),
        ),
      "Compétences inconnues ou incompatibles avec le niveau.",
    );
    assert(
      p.estimatedMinutes === null ||
        (Number.isFinite(p.estimatedMinutes) && p.estimatedMinutes > 0),
      "Durée invalide.",
    );
    const previous = s.activities.find((a) => a.id === p.id);
    const coefficient =
      p.coefficient ??
      p.package?.activity.coefficient ??
      previous?.coefficient ??
      1;
    assert(
      Number.isFinite(coefficient) && coefficient > 0,
      "Coefficient invalide.",
    );
    assert(
      (previous?.version ?? 0) === p.expectedVersion,
      "L’activité a changé. Recharge la bibliothèque avant de publier.",
    );
    let pkg = p.package ?? null;
    const content = normalizeActivityDraft(p);
    assert(content.blocks.length > 0 || content.checkpoints.length > 0, "Ajoute du contenu ou au moins un checkpoint.");
    if (pkg) {
      assert(
        pkg && validateInteractiveActivityPackage(pkg).length === 0,
        "Le contenu de l’activité est invalide.",
      );
      assert(
        pkg.activity.schoolLevel === p.levelTitle,
        "Le niveau du contenu ne correspond pas au classement.",
      );
      assert(
        pkg.activity.competencyIds.every((id) => p.competenceIds.includes(id)),
        "Le contenu utilise une compétence absente de l’activité.",
      );
      pkg = structuredClone(pkg);
      pkg.activity.title = p.title;
      pkg.activity.coefficient = coefficient;
      pkg.activity.theme = p.themeTitle;
      pkg.activity.chapter = p.chapterTitle;
    }
    if (p.blocks !== undefined || p.competencyCheckpoints !== undefined) {
      assert(!pkg, "Un seul format de contenu est attendu par publication.");
      const errors=validateInteractiveActivityPackage(activityPackageForEditing({...content,competencyCheckpoints:content.checkpoints}));
      assert(errors.length === 0, `Le contenu unifié est invalide : ${errors.join(' · ')}`);
    }
    const seeds = content.checkpoints;
    const checkpoints = seeds.map((c) => {
      const competence = catalog.find((item) => item.id === c.competencyId);
      assert(
        competence && p.competenceIds.includes(c.competencyId),
        "Compétence du checkpoint inconnue.",
      );
      const rubric = c.rubric ?? competence.criteria;
      assert(
        rubric &&
          ["A", "B", "C", "D"].every((l) =>
            text(rubric[l as keyof ValidationCriteria]),
          ),
        `Quatre critères explicites sont nécessaires pour ${competence?.code??c.competencyId} (${c.id}). Renseignez les critères A, B, C et D de cette compétence.`,
      );
      return {
        id: c.id,
        competencyId: c.competencyId,
        evidenceBlockIds: [...checkpointEvidenceIds(c)],
        ...(checkpointAnchorId(c) ? {positionAfterBlockId: checkpointAnchorId(c)} : {}),
        ...(c.flowPosition !== undefined ? {flowPosition:c.flowPosition} : {}),
        rubric: structuredClone(rubric),
        competenceCode: competence.code,
        competenceLabel: competence.label,
      };
    });
    const activity: LibraryActivity = {
      id: p.id,
      title: p.title,
      levelId: p.levelId,
      themeId: p.themeId,
      chapterId: p.chapterId,
      levelTitle: p.levelTitle,
      themeTitle: p.themeTitle,
      chapterTitle: p.chapterTitle,
      activityType: p.activityType ?? "standard",
      coefficient,
      competenceIds: [...p.competenceIds],
      estimatedMinutes: p.estimatedMinutes,
      version: (previous?.version ?? 0) + 1,
      archived: false,
      updatedAt: now,
    };
    s.activities = [...s.activities.filter((a) => a.id !== p.id), activity];
    s.versions.push({ ...activity, package: pkg, checkpoints,
      ...(p.blocks !== undefined || p.competencyCheckpoints !== undefined ?
        {blocks:structuredClone(content.blocks),assets:structuredClone(content.assets),competencyCheckpoints:structuredClone(content.checkpoints)} : {}) });
    return s;
  }
  if (command.type === "archive") {
    teacher(actor);
    const activity = s.activities.find((a) => a.id === command.activityId);
    assert(
      activity && typeof command.archived === "boolean",
      "Activité inconnue.",
    );
    activity.archived = command.archived;
    return s;
  }
  if (command.type === "assign") {
    teacher(actor);
    const a = command.assignment;
    assert(
      a &&
        text(a.id) &&
        text(a.targetId) &&
        ["student", "students", "group", "class"].includes(a.targetType),
      "Affectation invalide.",
    );
    assert(
      s.activities.some((v) => v.id === a.activityId && !v.archived),
      "Activité indisponible.",
    );
    assert(
      !s.assignments.some((v) => v.id === a.id),
      "Cette affectation existe déjà.",
    );
    assert(
      Array.isArray(a.recipients) &&
        a.recipients.length > 0 &&
        a.recipients.every((r) => text(r.classId) && text(r.studentId)),
      "Aucun destinataire valide.",
    );
    assert(
      a.dueDate === null ||
        (text(a.dueDate) && Number.isFinite(Date.parse(a.dueDate))),
      "Échéance invalide.",
    );
    const recipients = a.recipients
      .filter((r, i, all) => all.findIndex((v) => sameStudent(v, r)) === i)
      .map((r) => ({ classId: r.classId, studentId: r.studentId }));
    if (a.targetType === "student") assert(recipients.length === 1, "Un seul élève attendu.");
    s.assignments.push({
      id: a.id,
      activityId: a.activityId,
      targetType: a.targetType,
      targetId: a.targetType === "student" ? `${recipients[0].classId}:${recipients[0].studentId}` : a.targetType === "students" ? a.id : a.targetId,
      recipients,
      assignedAt: now,
      dueDate: a.dueDate,
    });
    return s;
  }
  if (command.type === "start") {
    const assignment = s.assignments.find((a) => a.id === command.assignmentId);
    assert(
      command.student &&
        assignment &&
        assignment.recipients.some((r) => sameStudent(r, command.student)),
      "Cette activité n’est pas affectée à cet élève.",
    );
    assert(
      actor.role === "teacher" || sameStudent(actor, command.student),
      "Accès refusé.",
    );
    const existing = s.attempts.find(
        (a) =>
          a.assignmentId === assignment.id && ownsAttempt(a, command.student),
      );
    assert((existing && attemptSessionId(existing)) || freeAccess(s, assignment, command.student.classId), "L’accès hors séance est fermé pour cette classe.");
    if (existing) {
      if (existing.status === "not_started") {
        assert(attemptAccess(s, existing, now) === "active", "Séance en pause ou terminée.");
        existing.status = "in_progress"; existing.startedAt = now; existing.revision += 1;
      }
      return s;
    }
    assert(!assignment.sessionId, "Réalisation de séance introuvable.");
    const activity = s.activities.find((a) => a.id === assignment.activityId)!;
    s.attempts.push({
      id: crypto.randomUUID(),
      classId: command.student.classId,
      studentId: command.student.studentId,
      activityId: activity.id,
      activityVersion: activity.version,
      assignmentId: assignment.id,
      status: "in_progress",
      startedAt: now,
      submittedAt: null,
      completedAt: null,
      answers: {},
      evidence: [],
      competenceResults: {},
      feedback: [],
      grade: null,
      durationSeconds: null,
      revision: 1,
    });
    return s;
  }
  assert(
    ["share_graphyo", "chemical_lab_action", "experiment_action", "measure_instrument", "answer", "open_help", "evidence", "submit", "review", "complete", "annotate_photo", "rotate_photo", "annotate_text", "set_correction_override", "set_question_verdict"].includes(
      command.type,
    ),
    "Commande inconnue.",
  );
  const attempt = s.attempts.find((a) => a.id === command.attemptId);
  assert(
    attempt && (actor.role === "teacher" || ownsAttempt(attempt, actor)),
    "Réalisation inaccessible.",
  );
  if (command.type === "complete" && attempt.status === "completed") {
    teacher(actor);
    return s;
  }
  if (command.type === "rotate_photo") {
    teacher(actor);
    assert(["in_progress", "submitted", "reviewed", "completed"].includes(attempt.status), "Photo indisponible.");
    assert(command.expectedRevision === attempt.revision, "Ce travail a changé. Recharge avant de réessayer.");
    let target: AnswerPhoto | undefined;
    for (const photos of Object.values(attempt.photos ?? {})) {
      target = photos.find(photo => photo.id === command.photoId);
      if (target) break;
    }
    assert(target, "Photo inconnue pour ce travail.");
    const current = target.rotation ?? 0;
    const next = ((current + 90) % 360) as PhotoRotation;
    target.rotation = next;
    const annotation = attempt.photoAnnotations?.[target.id];
    if (annotation?.strokes?.length) {
      annotation.strokes = annotation.strokes.map(stroke => ({
        ...stroke,
        points: stroke.points.map(point => ({x: 1 - point.y, y: point.x})),
      }));
      annotation.updatedAt = now;
    }
    attempt.revision += 1;
    if (attempt.status !== "completed") attempt.lastSavedAt = now;
    return s;
  }
  assert(attempt.status !== "completed", "Une réalisation terminée est figée.");
  if (["share_graphyo", "chemical_lab_action", "experiment_action", "measure_instrument", "answer", "open_help", "evidence", "submit"].includes(command.type))
    assert(!attempt.focusGuard?.blocked, "Activité bloquée après une sortie. Seul le professeur peut la débloquer.");
  if (["share_graphyo", "chemical_lab_action", "experiment_action", "measure_instrument", "answer", "open_help", "evidence", "submit"].includes(command.type))
    assert(attemptAccess(s, attempt, now) === "active", "Séance en pause ou terminée. Les réponses enregistrées sont conservées.");
  if (actor.role === "student" && !attemptSessionId(attempt)) {
    const assignment = s.assignments.find(a => a.id === attempt.assignmentId);
    assert(assignment && freeAccess(s, assignment, attempt.classId), "L’accès hors séance est fermé pour cette classe.");
  }
  assert(
    command.expectedRevision === attempt.revision,
    "Ce travail a changé. Recharge avant de réessayer.",
  );
  const version = versionFor(s, attempt);
  const content = normalizeActivity(version);
  if(command.type==='share_graphyo'){
    assert(attempt.status==='in_progress','Le travail est déjà remis au professeur.');
    const block=content.blocks.find(b=>b.id===command.blockId);
    assert(block?.type==='graph_task'&&block.visible!==false&&block.graphyo?.classTable&&block.graphyo.preset==='density-calibration','Tableau de classe inconnu.');
    assert(!evidenceLocked(attempt,block.id),'Preuve verrouillée.');
    const session=s.digitalSessions?.find(d=>d.id===attemptSessionId(attempt)&&d.classId===attempt.classId&&d.activityId===attempt.activityId&&d.activityVersion===attempt.activityVersion);
    assert(session,'Le tableau commun est disponible pendant une séance de classe.');
    assert(Array.isArray(command.cells)&&command.cells.length>=1&&command.cells.length<=12,'Lot de mesures invalide.');
    const values={...session.sharedGraphyo?.[block.id]},own=densityValues(attempt.answers[block.id]??''),seen=new Set<number>();
    for(const cell of command.cells){
      assert(cell&&densityConcentrations.includes(cell.concentration)&&!seen.has(cell.concentration)&&Number.isFinite(cell.value)&&cell.value>0&&cell.value<=2000&&(cell.expected===null||Number.isFinite(cell.expected)),'Mesure partagée invalide.');
      seen.add(cell.concentration);
      const key=String(cell.concentration);
      assert(own[key]===cell.value,'Enregistrer la mesure dans votre tableau avant de la partager.');
      assert(values[key]===cell.value||(values[key]??null)===cell.expected,'Une mesure a changé : reprendre le tableau de la classe avant de repartager.');
      values[key]=cell.value;
    }
    session.sharedGraphyo={...session.sharedGraphyo,[block.id]:values};session.revision+=1;
    attempt.revision+=1;attempt.lastSavedAt=now;return s;
  }
  if(command.type==='chemical_lab_action'){
    assert(attempt.status==='in_progress','Le travail est déjà remis au professeur.');
    const block=content.blocks.find(b=>b.id===command.blockId);
    assert(block&&(block.type==='simulation'||block.type==='animation')&&block.visible!==false&&block.resourceId===chemicalLaboratoryId,'Laboratoire inconnu.');
    const target=block.measurementTargetBlockId;
    assert(!evidenceLocked(attempt,block.id)&&(!target||!evidenceLocked(attempt,target)),'Preuve verrouillée.');
    attempt.answers[block.id]=chemicalLabAction(block.resourceConfig!,attempt.answers[block.id]??'',command.action,Date.parse(now));
    if(target)attempt.answers[target]=mergeChemicalMeasurements(attempt.answers[block.id],attempt.answers[target]??'',block.id);
    attempt.lastSavedAt=now;attempt.revision+=1;return s;
  }
  if(command.type==='experiment_action'){
    assert(attempt.status==='in_progress','Le travail est déjà remis au professeur.');
    const block=content.blocks.find(b=>b.id===command.blockId);
    assert(block&&(block.type==='simulation'||block.type==='animation')&&block.visible!==false&&block.resourceId===measurementEngineId&&block.measurementTargetBlockId,'Expérience inconnue.');
    const target=block.measurementTargetBlockId;
    assert(!evidenceLocked(attempt,block.id)&&!evidenceLocked(attempt,target),'Preuve verrouillée.');
    const next=experimentAnswers(block.resourceConfig!,attempt.answers[block.id]??'',attempt.answers[target]??'',block.id,command.action,Date.parse(now));
    attempt.answers[block.id]=next.value;attempt.answers[target]=next.table;attempt.revision+=1;return s;
  }
  if (command.type === "measure_instrument") {
    assert(attempt.status === 'in_progress', 'Le travail est déjà remis au professeur.');
    const block=content.blocks.find(b=>b.id===command.blockId);
    assert(block && (block.type==='simulation'||block.type==='animation') && block.resourceId===spectroId && block.measurementTargetBlockId, 'Instrument inconnu.');
    const target=block.measurementTargetBlockId;
    assert(!evidenceLocked(attempt,target)&&!evidenceLocked(attempt,block.id), 'Preuve verrouillée.');
    const checked=validateExperimentConfig(spectroId,block.resourceConfig??{});
    assert(!checked.errors.length&&checked.config.mode!=='method', 'Configuration instrumentale invalide.');
    const known:Record<string,number>={S1:.002,S2:.004,S3:.006,S4:.008,S5:.01};
    assert(command.solutionId==='unknown'||Object.hasOwn(known,command.solutionId), 'Solution inconnue.');
    const wavelength=command.wavelengthNm;
    assert(Number.isFinite(wavelength)&&wavelength>=Number(checked.config.wavelengthMinNm)&&wavelength<=Number(checked.config.wavelengthMaxNm), 'Longueur d’onde invalide.');
    const concentration=command.solutionId==='unknown'?Number(checked.config.unknownConcentrationMolL):known[command.solutionId];
    const existing=sourceMeasurements(attempt.answers[target]??'',block.id);
    const measurement={measurementId:`${attempt.id}:${block.id}:${attempt.revision}`,measurementIndex:Math.max(0,...existing.map(m=>m.measurementIndex))+1,solutionId:command.solutionId,wavelengthNm:wavelength,concentrationMolL:command.solutionId==='unknown'?null:concentration,absorbance:Number(measuredAbsorbance(command.solutionId,concentration,wavelength,checked.config).toFixed(3)),blanked:true,unit:{wavelengthNm:'nm',absorbance:''}};
    attempt.answers[target]=applyMeasurementEvent(attempt.answers[target]??'',block.id,spectroId,checked.config,{type:'measurement_taken',payload:measurement});
    attempt.revision+=1;return s;
  }
  if (command.type === "annotate_photo") {
    teacher(actor);
    assert(["submitted", "reviewed"].includes(attempt.status), "L’élève doit d’abord remettre son travail.");
    assert(Object.values(attempt.photos ?? {}).some(photos => photos.some(p => p.id === command.photoId)), "Photo inconnue pour ce travail.");
    assert(validPhotoStrokes(command.strokes), "Annotations invalides ou trop volumineuses.");
    attempt.photoAnnotations = {...attempt.photoAnnotations, [command.photoId]: {strokes: structuredClone(command.strokes), updatedAt: now}};
    attempt.revision += 1;
    return s;
  }
  if (command.type === "set_question_verdict") {
    teacher(actor);
    assert(["in_progress","submitted","reviewed"].includes(attempt.status), "Correction figée.");
    assert(["green","orange","red"].includes(command.state), "État de correction invalide.");
    assert(content.blocks.some(b=>b.id===command.blockId && b.type==='question'), "Question inconnue.");
    attempt.questionVerdicts={...attempt.questionVerdicts,[command.blockId]:{state:command.state,updatedAt:now}};
    attempt.revision+=1; attempt.lastSavedAt=now;
    return s;
  }
  if (command.type === "annotate_text") {
    teacher(actor);
    assert(["in_progress", "submitted", "reviewed"].includes(attempt.status), "Cette copie ne peut plus recevoir d’annotation.");
    assert(typeof command.text === "string" && command.text.length <= 5000, "Annotation trop longue.");
    const block = content.blocks.find(b => b.id === command.blockId);
    assert(block && ["question", "measurement_table", "graph_task"].includes(block.type), "Production inconnue.");
    const value = command.text.trim();
    const annotations = {...attempt.teacherTextAnnotations};
    if (value) annotations[block.id] = {text: value, updatedAt: now};
    else delete annotations[block.id];
    attempt.teacherTextAnnotations = annotations;
    attempt.revision += 1;
    attempt.lastSavedAt = now;
    return s;
  }
  if (command.type === "set_correction_override") {
    teacher(actor);
    assert(["in_progress", "submitted", "reviewed"].includes(attempt.status), "Cette correction ne peut plus être modifiée.");
    assert(typeof command.text === "string" && command.text.length <= 10000, "Correction trop longue.");
    const block = content.blocks.find(b => b.id === command.blockId);
    assert(block?.type === "question", "Question inconnue.");
    const value = command.text.trim();
    const overrides = {...attempt.teacherCorrectionOverrides};
    if (value) overrides[block.id] = {text: value, updatedAt: now};
    else delete overrides[block.id];
    attempt.teacherCorrectionOverrides = overrides;
    attempt.revision += 1;
    attempt.lastSavedAt = now;
    return s;
  }
  if (command.type === "open_help") {
    assert(attempt.status === "in_progress", "Le travail est déjà remis au professeur.");
    const help = content.blocks.find(b => b.id === command.blockId);
    if (help?.type === "question" && help.progressiveHints?.length) {
      const level=command.helpLevel, prior=attempt.helpUsage?.[help.id];
      assert(Number.isInteger(level)&&level!>0&&level!<=help.progressiveHints.length,"Niveau d’aide invalide.");
      if(prior?.helpHistory?.some(h=>h.level===level)) return s;
      assert(level===(prior?.maxHelpLevel??0)+1,"Consulter les aides dans l’ordre.");
      assert(!evidenceLocked(attempt,help.id),"Preuve verrouillée par le professeur.");
      assert(typeof command.sourceAnswer==='string',"Réponse initiale invalide.");
      const saved=applyLibraryCommand(s,{type:'answer',attemptId:attempt.id,expectedRevision:attempt.revision,answers:{...command.answers,[help.id]:command.sourceAnswer}},actor,catalog,now);
      const updated=saved.attempts.find(a=>a.id===attempt.id)!;
      const initialPhotoIds=prior?.photoIdsSnapshot??(updated.photos?.[help.id]??[]).map(photo=>photo.id);
      updated.helpUsage={...updated.helpUsage,[help.id]:{helpUsed:true,progressive:true,helpOpenedAt:prior?.helpOpenedAt??now,sourceBlockId:help.id,answerSnapshot:prior?.answerSnapshot??updated.answers[help.id],...(initialPhotoIds.length?{photoIdsSnapshot:initialPhotoIds}:{}),maxHelpLevel:level,helpHistory:[...(prior?.helpHistory??[]),{level:level!,openedAt:now}]}};
      return saved;
    }
    assert(help?.type === "question" && help.hint && help.helpSnapshotSourceBlockId, "Aide suivie inconnue.");
    if (attempt.helpUsage?.[help.id]?.helpUsed) return s;
    assert(!evidenceLocked(attempt, help.id) && !evidenceLocked(attempt, help.helpSnapshotSourceBlockId), "Preuve verrouillée par le professeur.");
    assert(typeof command.sourceAnswer === "string" && command.sourceAnswer.trim(), "Rédige ta proposition initiale avant d’ouvrir l’aide.");
    // One atomic command: normal answer validation, snapshot, then opening metadata.
    // The timestamp comes from the command handler, never from the browser.
    const saved = applyLibraryCommand(s, {type: "answer", attemptId: attempt.id,
      expectedRevision: attempt.revision, answers: {...command.answers, [help.helpSnapshotSourceBlockId]: command.sourceAnswer}}, actor, catalog, now);
    const updated = saved.attempts.find(a => a.id === attempt.id)!;
    const initialPhotoIds = (updated.photos?.[help.helpSnapshotSourceBlockId] ?? []).map(photo => photo.id);
    updated.helpUsage = {...updated.helpUsage, [help.id]: {helpUsed: true, helpOpenedAt: now,
      sourceBlockId: help.helpSnapshotSourceBlockId, answerSnapshot: updated.answers[help.helpSnapshotSourceBlockId],
      ...(initialPhotoIds.length ? {photoIdsSnapshot: initialPhotoIds} : {})}};
    return saved;
  }
  if (command.type === "answer") {
    assert(
      attempt.status === "in_progress",
      "Le travail est déjà remis au professeur.",
    );
    assert(
      command.answers &&
        typeof command.answers === "object" &&
        !Array.isArray(command.answers),
      "Réponses invalides.",
    );
    for (const [id, value] of Object.entries(command.answers)) {
      assert(!evidenceLocked(attempt, id) || value === attempt.answers[id], "Preuve verrouillée par le professeur.");
      const block = content.blocks.find(
        (b) => b.id === id,
      );
      assert(
        block &&
          [
            "question",
            "measurement_table",
            "graph_task",
            "lab_instruction",
          ].includes(block.type) || block && (block.type==='simulation'||block.type==='animation') && block.lockAfterCompletion,
        "Type de production invalide.",
      );
      assert(
        block &&
          typeof value === "string" &&
          value.length <= (block.type==='graph_task'&&block.graphyo?GRAPHYO_MAX_LENGTH:block.type==='measurement_table'&&['ph','conductivity'].includes(block.measurementTableKind??'')?100000:20000),
        "Réponse invalide.",
      );
      if(block.type==='graph_task'&&block.graphyo)assert(!graphyoAnswerError(value,block.graphyo), "Document Graphyo invalide.");
      if (block.type === 'question' && block.diagramLabeling) {
        const error = titrationLabelingAnswerError(value, block.diagramLabeling);
        assert(!error, error ?? 'Réponse au schéma invalide.');
      }
      if ((block.type==='simulation'||block.type==='animation')&&block.lockAfterCompletion) {
        assert(!attempt.answers[id]||value===attempt.answers[id], 'Manipulation déjà validée : résultat figé.');
        assert(savedManipulation(value), 'Bilan de manipulation invalide.');
      }
      if (block.type === "question" && block.answerType === "mcq" && value) {
        let choices: unknown;
        try {
          choices = JSON.parse(value);
        } catch {
          throw new Error("Choix QCM invalides.");
        }
        assert(
          Array.isArray(choices) &&
            choices.every(
              (choice) =>
                typeof choice === "string" &&
                block.options?.some((option) => option.id === choice),
            ),
          "Choix QCM invalides.",
        );
      }
      if (block.type === "question" && block.answerType === "true_false")
        assert(
          ["", "true", "false"].includes(value),
          "Réponse vrai/faux invalide.",
        );
      if (block.type === "measurement_table" && block.resourceMeasurementTable && value) {
        let carnet: any;
        try { carnet=JSON.parse(value); } catch { throw new Error('Carnet de mesures invalide.'); }
        assert(carnet?.format==='resource-measures-v1'&&typeof carnet.text==='string'&&Array.isArray(carnet.rows)&&carnet.rows.length<=(block.maxRows??100)&&carnet.rows.every((row:any)=>row&&(!block.acquisitionOnly||row.source==='simulation')&&['manual','simulation'].includes(row.source)&& (row.source!=='simulation'||row.measurement&&typeof row.measurement.measurementId==='string')), 'Carnet de mesures invalide.');
        for (const sourceId of new Set(carnet.rows.filter((r:any)=>r.source==='simulation').map((r:any)=>r.sourceBlockId))) {
          const source=content.blocks.find(b=>b.id===sourceId);
          assert(source&&(source.type==='simulation'||source.type==='animation')&&source.measurementTargetBlockId===id&&source.resourceId, 'Source du carnet invalide.');
          if(source.resourceId===chemicalLaboratoryId){
            const acquired=chemicalMeasurementRows(attempt.answers[source.id]??'',source.id);
            assert(carnet.rows.filter((r:any)=>r.source==='simulation'&&r.sourceBlockId===sourceId).every((r:any)=>acquired.some(m=>JSON.stringify(m)===JSON.stringify(r))), 'Mesure expérimentale non acquise.');
            continue;
          }
          if(source.resourceId===measurementEngineId){
            const state=parseExperimentState(attempt.answers[source.id]??'');
            assert(decodeExperiment(source.resourceConfig!).transfer==='automatic'&&carnet.rows.filter((r:any)=>r.sourceBlockId===sourceId).every((r:any)=>r.resourceId===measurementEngineId&&state?.measurements.some(m=>JSON.stringify(m)===JSON.stringify(r.measurement))),'Mesure expérimentale non acquise.');
            continue;
          }
          const checked=validateExperimentConfig(source.resourceId,source.resourceConfig??{});
          const rows=carnet.rows.filter((r:any)=>r.source==='simulation'&&r.sourceBlockId===sourceId);
          assert(!checked.errors.length&&rows.every((r:any)=>r.resourceId===source.resourceId)&&validateSeries(source.resourceId,rows.map((r:any)=>r.measurement),checked.config), 'Mesures du carnet invalides.');
        }
      }
      if (block.type === "measurement_table" && !block.resourceMeasurementTable && value) {
        let rows: unknown;
        try {
          rows = JSON.parse(value);
        } catch {
          throw new Error("Tableau de mesures invalide.");
        }
        assert(
          Array.isArray(rows) &&
            rows.length <= (block.maxRows ?? 100) &&
            rows.every(
              (row) =>
                row &&
                typeof row === "object" &&
                !Array.isArray(row) &&
                Object.entries(row).every(
                  ([key, cell]) =>
                    block.columns.some((c) => c.id === key) &&
                    typeof cell === "string",
                ),
            ),
          "Tableau de mesures invalide.",
        );
      }
      assert(Object.values(attempt.helpUsage ?? {}).every(h => !h.helpUsed || h.progressive || h.sourceBlockId !== id || h.answerSnapshot === value),
        "La proposition initiale est figée depuis l’ouverture de l’aide. Modifie la proposition révisée.");
      attempt.answers[id] = value;
      if ("competencyIds" in block)
        for (const competencyId of block.competencyIds ?? []) {
          const evidenceId = `answer:${id}:${competencyId}`;
          attempt.evidence = attempt.evidence.filter(
            (e) => e.id !== evidenceId,
          );
          if (value.trim())
            attempt.evidence.push({
              id: evidenceId,
              sourceId: id,
              competencyId,
              value: value.slice(0, 1000),
              recordedAt: now,
              kind: "answer",
            });
        }
    }
  } else if (command.type === "evidence") {
    assert(attempt.status === "in_progress", "Le travail est déjà remis.");
    const e = command.evidence;
    assert(!e || !evidenceLocked(attempt, e.sourceId), "Preuve verrouillée par le professeur.");
    assert(
      e &&
        text(e.id) &&
        text(e.sourceId) &&
        text(e.value) &&
        version.competenceIds.includes(e.competencyId),
      "Preuve invalide.",
    );
    assert(
      ["answer", "interaction", "observation"].includes(e.kind),
      "Type de preuve invalide.",
    );
    assert(
      attempt.evidence.length < 500,
      "Limite de preuves atteinte pour cette réalisation.",
    );
    if (e.kind === "observation") teacher(actor);
    else
      assert(
        content.blocks.some((b) => b.id === e.sourceId),
        "Source de preuve inconnue.",
      );
    if (!attempt.evidence.some((p) => p.id === e.id))
      attempt.evidence.push({ ...e, recordedAt: now });
  } else if (command.type === "submit") {
    assert(
      attempt.status === "in_progress",
      "Le travail ne peut plus être remis.",
    );
    attempt.status = "submitted";
    attempt.submittedAt = now;
  } else if (command.type === "review") {
    teacher(actor);
    assert(
      attempt.status === "submitted" ||
        attempt.status === "reviewed" ||
        content.capabilities.supportsDirectEvaluation,
      "L’élève doit d’abord remettre son travail.",
    );
    assert(
      Array.isArray(command.results) &&
        Array.isArray(command.feedback) &&
        command.feedback.every(
          (f) => typeof f === "string" && f.length <= 20000,
        ),
      "Évaluation invalide.",
    );
    assert(
      command.grade === null ||
        (Number.isFinite(command.grade) &&
          command.grade >= 0 &&
          command.grade <= 20),
      "Note invalide (0 à 20).",
    );
    for (const result of command.results) {
      const cp = version.checkpoints.find((c) => c.id === result.checkpointId);
      assert(
        cp &&
          levels.includes(result.level) &&
          typeof result.comment === "string" &&
          result.comment.length <= 20000,
        "Validation de checkpoint invalide.",
      );
      attempt.competenceResults[cp.id] = {
        checkpointId: cp.id,
        competencyId: cp.competencyId,
        level: result.level,
        comment: result.comment,
        validatedAt: now,
      };
      // A deliberate individual correction takes precedence over its automatic absence.
      if (attempt.ownerType !== "group" && attempt.attendance?.[attempt.studentId])
        delete attempt.attendance[attempt.studentId].results[cp.id];
    }
    attempt.feedback = command.feedback;
    attempt.grade = command.grade;
    attempt.status = "reviewed";
  } else {
    teacher(actor);
    assert(
      attempt.status === "reviewed",
      "Le professeur doit relire le travail avant de le finaliser.",
    );
    // Partial correction is allowed: only explicitly evaluated checkpoints are
    // written to the competency history. Missing checkpoints stay "Non évalué"
    // in the released copy and do not create synthetic history entries.
    finalizeAttempt(s, attempt, version, now);
  }
  attempt.revision += 1;
  attempt.lastSavedAt = now;
  return s;
}

// Student projections contain neither peers' work nor corrections before finalization.
export function studentLibrary(
  source: LibraryState,
  student: StudentRef,
): LibraryState {
  const s = structuredClone(source);
  s.assignments = s.assignments
    .filter((a) => a.recipients.some((r) => sameStudent(r, student)))
    .map((a) => ({ ...a, recipients: [student] }));
  s.attempts = s.attempts.filter((a) => ownsAttempt(a, student));
  s.digitalSessions = s.digitalSessions
    ?.filter(d => s.attempts.some(a => attemptSessionId(a) === d.id))
    .map(d => {
      const copy = {...d};
      delete copy.participation;
      delete copy.attemptIds;
      return copy;
    });
  s.history = s.history.filter((a) => sameStudent(a, student));
  const ids = new Set(s.assignments.map((a) => a.activityId));
  s.activities = s.activities.filter((a) => ids.has(a.id));
  const latestVersions = new Map(
    s.activities.map((activity) => [
      activity.id,
      s.versions.find(
        (version) =>
          version.id === activity.id && version.version === activity.version,
      ),
    ]),
  );
  s.versions = s.versions.filter((v) =>
    s.attempts.some(
      (a) => a.activityId === v.id && a.activityVersion === v.version,
    ),
  );
  for (const v of s.versions) {
    if (v.package) delete v.package.interactive.aiImport;
    const blocks = [...(v.blocks ?? []), ...(v.package?.interactive.blocks ?? [])];
    const released = s.attempts.some(
      (attempt) =>
        attempt.activityId === v.id &&
        attempt.activityVersion === v.version &&
        copyReleased(attempt),
    );
    const latest = latestVersions.get(v.id);
    const newerCorrectionRevision =
      (latest?.package?.interactive.revision ?? 0) >
      (v.package?.interactive.revision ?? 0);
    if (released && latest && (latest.version !== v.version || newerCorrectionRevision)) {
      const latestBlocks = [
        ...(latest.blocks ?? []),
        ...(latest.package?.interactive.blocks ?? []),
      ];
      for (const block of blocks) {
        if (block.type !== "question" && block.type !== "measurement_table") continue;
        const current = latestBlocks.find(
          (candidate) =>
            candidate.type === block.type &&
            candidate.id === block.id &&
            candidate.prompt === block.prompt,
        );
        if (current?.type !== "question" && current?.type !== "measurement_table") continue;
        if (
          block.type === "question" && current.type === "question" && current.correction &&
          (newerCorrectionRevision || !block.correction?.explanation?.trim())
        )
          block.correction = structuredClone(current.correction);
        if (
          current.studentCorrection &&
          (newerCorrectionRevision || !block.studentCorrection)
        )
          block.studentCorrection = current.studentCorrection;
        if(block.type==='question'&&current.type==='question'&&(newerCorrectionRevision||!block.correctionDiagram)&&current.correctionDiagram)block.correctionDiagram=structuredClone(current.correctionDiagram);
        if(block.type==='question'&&current.type==='question'&&(newerCorrectionRevision||!block.correctionGraphConfig)&&current.correctionGraphConfig)block.correctionGraphConfig=structuredClone(current.correctionGraphConfig);
      }
    }
    for (const block of blocks) {
      delete block.teacherNote; delete block.teacherGuidance;
      if((block.type==='simulation'||block.type==='animation')&&(block.resourceId===measurementEngineId||block.resourceId===chemicalLaboratoryId)&&block.resourceConfig){
        const c=decodeExperiment(block.resourceConfig);
        block.resourceConfig=serializePublicExperimentConfig(c,released?'correction':'student');
      }
      if ((block.type==='simulation'||block.type==='animation') && block.resourceId===spectroId && block.resourceVersion===3 && block.resourceConfig) {
        delete block.resourceConfig.unknownConcentrationMolL;
        delete block.resourceConfig.slopeAtLambdaMax;
        delete block.resourceConfig.lambdaMaxNm;
        delete block.resourceConfig.noiseAmplitude;
      }
    }
    const completed = s.attempts.some(
      (a) =>
        a.activityId === v.id &&
        a.activityVersion === v.version &&
        copyReleased(a),
    );
    if (!completed) {
      for (const block of blocks) {
        if (block.visible === false) {
          if ('content' in block) block.content = '';
          if (block.type === 'text') delete block.illustrations;
          if ('prompt' in block) block.prompt = '';
        }
        if (block.type === 'measurement_table' && block.calibrationSeries && block.dataSource !== 'provided' && v.package?.interactive.experimentalMode !== 'provided_data') {
          for (const seed of block.calibrationSeries) {delete seed.absorbance;delete seed.conductivity;delete seed.samplingVolumeMl;}
        }
        if ("correctionGraphConfig" in block) delete block.correctionGraphConfig;
        if ("correctionDiagram" in block) delete block.correctionDiagram;
        delete block.correctionIllustrations;
        if (block.type === "graph_task" && block.graphConfig?.constructions) block.graphConfig.constructions.correctionObjects = [];
        if ("correction" in block) delete block.correction;
        if (block.type==='question' && block.progressTable) {
          for (const species of block.progressTable.species) {delete species.initial;delete species.coefficient;}
          if (block.progressTable.equationSourceBlockId) block.progressTable.equation='';
        }
        if ("studentCorrection" in block) delete block.studentCorrection;
        if ("expectedRelationships" in block)
          delete block.expectedRelationships;
        if (block.type === "measurement_table")
          for (const column of block.columns) delete column.expected;
        if ("options" in block && Array.isArray(block.options))
          for (const option of block.options)
            if (typeof option === "object")
              delete (option as { correct?: boolean }).correct;
      }
      if (v.package) v.package.interactive.competencyRules = [];
    }
  }
  for (const a of s.attempts) {
    const privateVersion=versionFor(source,a);
    const configurations=new Map<string,ReturnType<typeof decodeExperiment>>();
    for(const block of privateVersion?normalizeActivity(privateVersion).blocks:[]){
      if((block.type!=='simulation'&&block.type!=='animation')||block.resourceId!==chemicalLaboratoryId)continue;
      const c=decodeExperiment(block.resourceConfig!);
      configurations.set(block.id,c);
      a.answers[block.id]??=serializeChemicalWorld(initialChemicalWorld(c));
    }
    // Live updates sometimes contain attempts only. Redact even without a version;
    // absence of teacher metadata must never expose a saved chemical state.
    const project=(id:string,value:string)=>{const world=parseChemicalWorld(value);return world?serializeChemicalWorld(publicChemicalWorld(world,configurations.get(id))):value;};
    for(const [id,value] of Object.entries(a.answers))a.answers[id]=project(id,value);
    for(const e of a.evidence??[])e.value=project(e.sourceId,e.value);
    for(const h of Object.values(a.helpUsage??{}))h.answerSnapshot=project(h.sourceBlockId,h.answerSnapshot);
    a.copyAccessible = canConsultCopy(s,a);
    a.competenceResults = Object.fromEntries(Object.entries(resultsForMember(a, student.studentId)).filter(([,r])=>r && typeof r === "object" && !!r.validatedAt && levels.includes(r.level)));
    // Do not disclose another member's individual evaluation or automatic overrides.
    delete a.attendance;
    if (!copyReleased(a)) {
      delete a.photoAnnotations;
      delete a.teacherTextAnnotations;
      delete a.teacherCorrectionOverrides;
      delete a.questionVerdicts;
      for (const r of Object.values(a.competenceResults)) r.comment = "";
      a.feedback = [];
      a.grade = null;
    }
    if (!a.copyAccessible) {a.answers = {}; a.photos = {}; a.evidence = []; a.helpUsage = {};}
  }
  for (const h of s.history) if (!s.attempts.some(a => a.id === h.attemptId && copyReleased(a))) h.comment = "";
  return s;
}

export function classification(level: string, theme: string, chapter: string, curriculum: Curriculum = defaultCurriculum) {
  const themes =
    curriculum[level as keyof Curriculum] ?? [];
  const found =
    themes.find((t) => t.title === theme) ??
    themes.find((t) => t.chapters.some((c) => c.title === chapter));
  const slug = (s: string) =>
    encodeURIComponent(s.trim().toLocaleLowerCase("fr"));
  return {
    levelId: slug(level),
    themeId: found?.id ?? `${slug(level)}:${slug(theme || "À classer")}`,
    chapterId:
      found?.chapters.find((c) => c.title === chapter)?.id ??
      `${slug(level)}:${slug(theme)}:${slug(chapter || "À classer")}`,
    levelTitle: level,
    themeTitle: found?.title ?? (theme || "À classer"),
    chapterTitle: chapter || "À classer",
  };
}

export function legacyActivity(
  activity: ActivityDefinition,
  catalog: CatalogCompetency[],
): PublishActivity {
  const extended = activity as ActivityDefinition & {
    theme?: string;
    interactive?: InteractiveActivityPackage["interactive"];
  };
  const competenceIds = activity.competencyIds.filter((id) =>
    catalog.some((c) => c.id === id && c.schoolLevel === activity.schoolLevel),
  );
  const metadata = {
    title: activity.title,
    schoolLevel: activity.schoolLevel,
    theme: extended.theme ?? "À classer",
    chapter: activity.chapter || "À classer",
    coefficient: activity.coefficient || 1,
    competencyIds: competenceIds,
  };
  const pkg: InteractiveActivityPackage | null = extended.interactive
    ? {
        format: "competenceo-interactive-activity",
        version: 1,
        activity: metadata,
        interactive: extended.interactive,
      }
    : competenceIds.length
      ? null
      : {
          format: "competenceo-interactive-activity",
          version: 1,
          activity: metadata,
          interactive: {
            schemaVersion: 1,
            revision: 1,
            status: "draft",
            flow: "linear",
            assets: [],
            competencyRules: [],
            blocks: [
              {
                id: "legacy-content",
                type: "text",
                content:
                  "Cette activité historique ne comportait pas d’énoncé numérique enregistré. Le professeur peut le compléter dans le Studio.",
              },
            ],
          },
        };
  return {
    id: activity.id,
    expectedVersion: 0,
    title: activity.title,
    ...classification(
      activity.schoolLevel,
      extended.theme ?? "",
      activity.chapter,
    ),
    activityType: extended.interactive
      ? "interactive"
      : competenceIds.length
        ? "competency_only"
        : "standard",
    competenceIds,
    estimatedMinutes: null,
    coefficient: activity.coefficient || 1,
    package: pkg,
  };
}



